// @greenlight-spec: 28-it-dashboard-ui.md
import { type SortState } from '@/components/greenlight/DataList';
import {
  APP_SORT_KEYS,
  BUCKET_STATUSES,
  type AppListItem,
  type AppSortKey,
  type AppStatus,
  type AppStatusCounts,
  type FleetBucket,
} from '@greenlight/shared';

/** Inclusive = match ANY selected tag (union); exclusive = match ALL (intersection). */
export type TagMode = 'any' | 'all';

export interface PageOrderState {
  sort: SortState | null;
  /** Per-app active-user count over the usage window — backs the `usage` sort key. */
  usageByApp?: ReadonlyMap<string, number>;
  /**
   * Per-app last-used instant as epoch ms — backs the `lastUsed` sort key.
   * Pre-ranked by the caller (see `lastUsedSortValue`), so "never used" and
   * "not loaded" already order apart from each other and from real timestamps.
   */
  lastUsedByApp?: ReadonlyMap<string, number>;
}

export function bucketCount(counts: AppStatusCounts, bucket: FleetBucket): number {
  return BUCKET_STATUSES[bucket].reduce((sum, s) => sum + (counts[s] ?? 0), 0);
}

/**
 * Client mirror of the server's bucket rule: `needs_attention` also holds live apps whose
 * latest pipeline run failed (docs/28 §/apps).
 */
export function inBucket(
  app: { status?: string; last_pipeline_status?: string | null },
  bucket: FleetBucket,
): boolean {
  if (app.status === undefined) return false;
  if ((BUCKET_STATUSES[bucket] as readonly string[]).includes(app.status)) return true;
  return (
    bucket === 'needs_attention' &&
    app.status !== 'deleted' &&
    app.last_pipeline_status === 'failed'
  );
}

export function isFleetBucket(v: string | null): v is FleetBucket {
  return v != null && Object.prototype.hasOwnProperty.call(BUCKET_STATUSES, v);
}

/** Sort keys the server can order by; anything else stays a page-local sort. */
export function isServerSortKey(key: string): key is AppSortKey {
  return (APP_SORT_KEYS as readonly string[]).includes(key);
}

export function parseSort(raw: string | null): SortState | null {
  if (!raw) return null;
  const [key, direction] = raw.split(':');
  if (!key) return null;
  return { key, direction: direction === 'desc' ? 'desc' : 'asc' };
}

function sortValue(app: AppListItem, key: string): string {
  switch (key) {
    case 'slug':
      return app.slug;
    case 'status':
      return app.status;
    default:
      return '';
  }
}

// The status / bucket / search predicate for views that load their whole result
// set in one read — the fleet map and the marketplace grid. The apps list gets
// the same narrowing from the server (docs/16 §Pagination), so it does not use
// this; the semantics here are the client-side mirror of `appListFilterConditions`
// in the server's app-state service and must stay in step with it (docs/28 §/apps).
//
// Operates on any record carrying slug/name — the map's FleetGraphApp or
// MarketplaceApp (which has no `status`; passing `status: ''`/`bucket: ''` makes
// every status/bucket/deleted-exclusion branch below a no-op).
export function filterAppsByState<
  T extends {
    status?: string;
    last_pipeline_status?: string | null;
    slug: string;
    serving_subdomain?: string | null;
    name: string;
    tags?: readonly string[];
  },
>(
  items: readonly T[],
  {
    status,
    bucket,
    q,
    tags = [],
    tagMode = 'any',
  }: {
    status: AppStatus | '';
    bucket: FleetBucket | '';
    q: string;
    tags?: readonly string[];
    tagMode?: TagMode;
  },
): T[] {
  let out = [...items];
  if (status) out = out.filter((a) => a.status === status);
  if (bucket) out = out.filter((a) => inBucket(a, bucket));
  // `deleted` is archived (docs/15 §apps): hidden from every view except the
  // explicit "Deleted" chip or a `status=deleted` deep link. Selecting the
  // Deleted chip is the only in-UI way a deleted app appears in the list.
  if (bucket !== 'deleted' && status !== 'deleted') {
    out = out.filter((a) => a.status !== 'deleted');
  }
  if (q) {
    const needle = q.toLowerCase();
    out = out.filter(
      (a) =>
        a.slug.toLowerCase().includes(needle) ||
        a.name.toLowerCase().includes(needle) ||
        (a.serving_subdomain?.toLowerCase().includes(needle) ?? false),
    );
  }
  // Tag filter (ADR-0032): inclusive matches ANY selected tag, exclusive ALL.
  // Case-insensitive so a filter selection matches regardless of stored casing.
  if (tags.length) {
    const wanted = tags.map((t) => t.toLowerCase());
    out = out.filter((a) => {
      const appTags = (a.tags ?? []).map((t) => t.toLowerCase());
      return tagMode === 'all'
        ? wanted.every((t) => appTags.includes(t))
        : wanted.some((t) => appTags.includes(t));
    });
  }
  return out;
}

// Orders one already-narrowed page of the apps list. Every narrowing dimension —
// status, bucket, tag, search, hidden apps — is applied by the query, so what is
// left here is presentation: the favorites tier and the column sort (which still
// shapes the loaded page, since server-side sort keys beyond the default are not
// in the contract yet).
export function orderPage(
  items: AppListItem[],
  { sort, usageByApp, lastUsedByApp }: PageOrderState,
): AppListItem[] {
  const out = [...items];
  // Favorites are always a leading tier, client-enforced rather than trusted
  // from the page's own order — the real server pre-tiers via listApps, but
  // nothing here should depend on that; a stable partition is a no-op against
  // an already-tiered page and correct against one that isn't (docs/28 §/apps
  // Personalization).
  const favs = out.filter((a) => a.favorite);
  const rest = out.filter((a) => !a.favorite);
  if (sort) {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const comparator =
      sort.key === 'usage'
        ? // Numeric sort over the usage summary; an app with no summary row (e.g.
          // the read failed for it) orders below a genuine zero, so an idle app
          // and an unreadable one never collapse into the same position.
          (a: AppListItem, b: AppListItem) =>
            ((usageByApp?.get(a.app_id) ?? -1) - (usageByApp?.get(b.app_id) ?? -1)) * dir
        : sort.key === 'lastUsed'
          ? // Ascending reads oldest-first — the stale end of the fleet, which is
            // the reason to sort on this column at all.
            (a: AppListItem, b: AppListItem) =>
              ((lastUsedByApp?.get(a.app_id) ?? -1) - (lastUsedByApp?.get(b.app_id) ?? -1)) * dir
          : (a: AppListItem, b: AppListItem) =>
              sortValue(a, sort.key).localeCompare(sortValue(b, sort.key)) * dir;
    // The chosen sort applies within each tier, not across it.
    return [...favs.sort(comparator), ...rest.sort(comparator)];
  }
  return [...favs, ...rest];
}
